import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  workOrders as api, clients as clientsApi,
  type WorkOrder, type WorkOrderFile, type WorkOrderMeta, type FieldWork,
} from '@/api/endpoints';
import type { Client } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { ZlecenieFormModal } from '@/components/ZlecenieFormModal';
import { WorkOrderTimelineCard } from '@/components/WorkOrderTimelineCard';
import { validateUploadSize } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { xhrUpload } from '@/api/xhrUpload';
import { formatCurrency } from '@/lib/money';

const fmtSize = (b?: number | null) =>
  b == null ? '' : b < 1024 * 1024 ? `${Math.round(b / 1024)} kB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

/**
 * Karta zlecenia na roboty (V156).
 *
 * Zlecenie nie ma własnego statusu — panel „Stan" pokazuje to, co wynika z jego prac. Gdy prac nie
 * ma, mówimy to wprost i podsuwamy przycisk założenia pierwszej: zlecenie bez rozpisanych prac
 * jest najczęstszym sposobem, w jaki robota wypada z pola widzenia.
 */
export function ZlecenieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const orderId = id ? Number(id) : null;
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'FIELDWORK_ORDER_EDIT');
  const canEditTimeline = hasPermission(user, 'FIELDWORK_EDIT');
  const canSeePricing = hasPermission(user, 'PRICING_VIEW');
  const canSeeOffers = hasPermission(user, 'OFFER_VIEW');

  const [order, setOrder] = useState<WorkOrder | null>(null);
  const [works, setWorks] = useState<FieldWork[] | null>(null);
  const [files, setFiles] = useState<WorkOrderFile[] | null>(null);
  const [meta, setMeta] = useState<WorkOrderMeta | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    if (!orderId) return;
    setErr(null);
    api.get(orderId).then(setOrder).catch(e => setErr(e?.message ?? String(e)));
    api.works(orderId).then(setWorks).catch(() => setWorks([]));
    api.files(orderId).then(setFiles).catch(() => setFiles([]));
  };

  useEffect(() => { setOrder(null); reload(); /* eslint-disable-next-line */ }, [orderId]);
  useEffect(() => {
    api.meta().then(setMeta).catch(() => {});
    clientsApi.list().then(setClients).catch(() => {});
  }, []);

  const upload = (file: File) => {
    if (!orderId) return;
    const problem = validateUploadSize(file);
    if (problem) { setErr(problem); return; }
    setErr(null);
    setUploadPct(0);
    // Multipart, nie JSON — backend czyta plik przez @RequestParam("file").
    const fd = new FormData();
    fd.append('file', file);
    xhrUpload<WorkOrderFile>(`/api/work-orders/${orderId}/files`, fd, {
      onProgress: p => setUploadPct(p.percent),
    })
      .then(() => { setUploadPct(null); reload(); })
      .catch(e => { setUploadPct(null); setErr(e?.message ?? String(e)); });
  };

  const cancel = () => {
    if (!order) return;
    const reason = prompt(`Anulować zlecenie ${order.number}? Podaj powód (opcjonalnie):`);
    if (reason === null) return;
    api.cancel(order.id, reason || null).then(setOrder).catch(e => setErr(e?.message ?? String(e)));
  };

  const remove = () => {
    if (!order) return;
    if (!confirm(`Usunąć zlecenie ${order.number}? Operacji nie da się cofnąć.`)) return;
    api.remove(order.id).then(() => nav('/zlecenia')).catch(e => setErr(e?.message ?? String(e)));
  };

  if (err && !order) return <div className="text-red-600">Błąd: {err}</div>;
  if (!order) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="max-w-[1300px]">
      <header className="flex items-start justify-between mb-5 gap-4">
        <div className="min-w-0">
          <div className="text-[12px] text-ink-500 mb-1">
            <NavLink to="/zlecenia" className="hover:text-ink-800">Zlecenia</NavLink>
            <span className="mx-1.5">/</span>
            <span className="text-ink-700 font-mono">{order.number}</span>
          </div>
          <h1 className="m-0">{order.title}</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-500 mt-2">
            {order.cancelled ? (
              <span className="pill pill-earth">Anulowane{order.cancelReason ? ` — ${order.cancelReason}` : ''}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: order.statusColor }}/>
                {order.derivedStatusName}
              </span>
            )}
            <span className="pill pill-earth">{order.receivedViaName}</span>
            {order.clientId && (
              <NavLink to={`/klienci/${order.clientId}`} className="text-water-600 hover:underline">
                {order.clientName}
              </NavLink>
            )}
            {order.clientOrderRef && <span>nr u klienta: <strong>{order.clientOrderRef}</strong></span>}
            {order.receivedByName && <span>przyjął: {order.receivedByName}</span>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {canEdit && !order.cancelled && (
            <button onClick={() => setEditOpen(true)} className="btn btn-secondary btn-sm">
              <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj
            </button>
          )}
          {canEdit && !order.cancelled && (
            <button onClick={cancel} className="btn btn-secondary btn-sm">Anuluj zlecenie</button>
          )}
          {canEdit && order.cancelled && (
            <button onClick={() => api.restore(order.id).then(setOrder)} className="btn btn-secondary btn-sm">
              Przywróć
            </button>
          )}
          {canEdit && order.worksTotal === 0 && (
            <button onClick={remove} className="btn btn-ghost btn-sm text-red-600" title="Usuń zlecenie">
              <Icon name="x" className="w-3.5 h-3.5"/>
            </button>
          )}
          <NavLink to="/zlecenia" className="btn btn-secondary btn-sm">← Rejestr</NavLink>
        </div>
      </header>

      {err && <div className="text-red-600 p-3 bg-red-50 border border-red-200 rounded mb-3">{err}</div>}

      <div className="grid grid-cols-[1fr_340px] gap-4 items-start">
        <div className="grid gap-4">
          <WorkOrderTimelineCard key={works?.map(w => w.id + w.status).join() ?? ''}
                                 orderId={order.id} canEdit={canEditTimeline && !order.cancelled}/>
          <div className="card">
            <div className="card-head">
              <div className="card-title">Prace z tego zlecenia</div>
              <span className="ml-auto text-[12px] text-ink-400">{works?.length ?? 0}</span>
            </div>
            <div className="card-body !p-0">
              {works == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
              {works && works.length === 0 && (
                <div className="p-5 text-[13px]">
                  <div className="text-amber-700 font-medium mb-1">
                    Do tego zlecenia nie rozpisano jeszcze żadnej pracy.
                  </div>
                  <div className="text-ink-500 mb-3">
                    Dopóki praca nie powstanie, robota nie pojawi się w rejestrze, na mapie ani
                    na harmonogramie.
                  </div>
                  <NavLink to={`/prace?clientId=${order.clientId ?? ''}`} className="btn btn-primary btn-sm">
                    <Icon name="plus" className="w-3.5 h-3.5"/> Przejdź do rejestru prac
                  </NavLink>
                </div>
              )}
              {works && works.length > 0 && (
                <table className="table-hw">
                  <thead>
                    <tr><th>Lp.</th><th>Opis prac</th><th>Rodzaj</th><th>Status</th><th>Wykonano</th><th></th></tr>
                  </thead>
                  <tbody>
                    {works.map(w => (
                      <tr key={w.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => nav(`/prace/${w.id}`)}>
                        <td className="font-mono text-[12px] text-ink-500">{w.seqNo}</td>
                        <td className="text-[13px] text-ink-800 truncate max-w-[320px]">
                          {w.title}
                          {w.wellLabel && <span className="text-ink-400"> · studnia {w.wellLabel}</span>}
                        </td>
                        <td><span className="pill pill-earth">{w.kindName}</span></td>
                        <td><span className={'pill pill-' + w.statusColor}>{w.statusName}</span></td>
                        <td className="font-mono text-[12px] text-ink-600">{w.firstDate ?? '—'}</td>
                        <td><Icon name="chevron" className="w-3 h-3 text-ink-400"/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Skan zlecenia</div>
              <span className="ml-auto text-[12px] text-ink-400">{files?.length ?? 0}</span>
            </div>
            <div className="card-body">
              {files && files.length === 0 && (
                <div className="text-[13px] text-ink-500 mb-3">
                  Brak dokumentu. To normalne przy zleceniu przyjętym telefonicznie — skan można
                  dosłać później.
                </div>
              )}
              {files && files.length > 0 && (
                <ul className="divide-y divide-ink-100 mb-3">
                  {files.map(f => (
                    <li key={f.id} className="flex items-center gap-2 py-2 text-[13px]">
                      <Icon name="file-text" className="w-4 h-4 text-ink-400"/>
                      <a href={`/api/archive/documents/${f.documentId}/download`}
                         className="text-water-600 hover:underline flex-1 truncate">{f.filename}</a>
                      <span className="text-[11px] text-ink-400">{fmtSize(f.sizeBytes)}</span>
                      {canEdit && (
                        <button onClick={() => api.removeFile(f.id).then(reload)}
                                className="btn btn-ghost btn-sm text-ink-400 hover:text-red-600" title="Odepnij">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {canEdit && (
                <>
                  <label className="btn btn-secondary btn-sm cursor-pointer">
                    <Icon name="plus" className="w-3.5 h-3.5"/> Wgraj skan
                    <input type="file" className="hidden"
                           onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}/>
                  </label>
                  {uploadPct != null && (
                    <div className="mt-2">
                      <UploadProgressBar percent={uploadPct} label="Wysyłanie skanu…" compact/>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {(order.description || order.notes) && (
            <div className="card">
              <div className="card-head"><div className="card-title">Ustalenia i uwagi</div></div>
              <div className="card-body text-[13px] text-ink-700 whitespace-pre-wrap">
                {order.description}
                {order.notes && (
                  <div className="mt-3 pt-3 border-t border-ink-100 text-ink-600 italic">{order.notes}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <aside className="grid gap-4">
          <div className="card">
            <div className="card-head"><div className="card-title">Zlecenie</div></div>
            <div className="card-body text-[13px] grid gap-2">
              <Row label="Data zlecenia" value={order.orderDate}/>
              <Row label="Jak wpłynęło" value={order.receivedViaName}/>
              <Row label="Termin realizacji" value={order.deadline ?? '—'}
                   tone={order.daysToDeadline != null && order.daysToDeadline < 0 ? 'red' : undefined}
                   hint={order.daysToDeadline == null ? undefined
                     : order.daysToDeadline < 0 ? `po terminie o ${-order.daysToDeadline} dni`
                     : `pozostało ${order.daysToDeadline} dni`}/>
              {canSeePricing && (
                <Row label="Wartość netto"
                     value={order.netAmount == null && order.priceVariants?.length ? 'nieustalona' : formatCurrency(order.netAmount)}
                     hint={order.priceVariants?.length ? 'suma rozliczanych wariantów' : undefined}/>
              )}
              {canSeePricing && !!order.priceVariants?.length && (
                <div className="grid gap-1 border-t border-ink-100 pt-2">
                  <div className="text-[11px] text-ink-400">Warianty / etapy z oferty</div>
                  {order.priceVariants.map((v, i) => (
                    <div key={i} className={'flex justify-between gap-3 text-[12px] '
                      + (v.billed ? 'text-ink-800' : 'text-ink-400')}>
                      <span className="flex items-start gap-1.5 min-w-0">
                        <span className="w-3.5 shrink-0" title={v.billed ? 'Rozliczany' : 'Nierozliczany'}>
                          {v.billed && <Icon name="check" className="w-3.5 h-3.5 text-green-700"/>}
                        </span>
                        <span>{v.label}</span>
                      </span>
                      <span className="font-mono whitespace-nowrap">{formatCurrency(v.netAmount)}</span>
                    </div>
                  ))}
                </div>
              )}
              {canSeeOffers && (
                <div className="flex justify-between gap-3">
                  <span className="text-ink-500">Z oferty</span>
                  <span className="text-right">
                    {order.sourceOfferId
                      ? <NavLink to={`/oferty/${order.sourceOfferId}`} className="text-water-600 hover:underline font-mono">
                          {order.sourceOfferNumber}
                        </NavLink>
                      : order.sourceOfferRef
                        ? <>
                            <span className="font-mono text-ink-800">{order.sourceOfferRef}</span>
                            <div className="text-[11px] text-ink-400">spoza portalu</div>
                          </>
                        : <span className="text-ink-500">bez oferty</span>}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head"><div className="card-title">Stan prac</div></div>
            <div className="card-body text-[12px] text-ink-700 grid gap-1">
              <div className="flex justify-between"><span>Wszystkich prac:</span><strong>{order.worksTotal}</strong></div>
              <div className="flex justify-between"><span>W toku:</span><strong className="text-orange-600">{order.worksInProgress}</strong></div>
              <div className="flex justify-between"><span>Zlecone:</span><strong className="text-red-600">{order.worksOrdered}</strong></div>
              <div className="flex justify-between"><span>Zakończone:</span><strong className="text-green-700">{order.worksDone}</strong></div>
              {order.worksOverdue > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Po terminie umowy:</span><strong>{order.worksOverdue}</strong>
                </div>
              )}
              {order.worksTotal > 0 && (
                <NavLink to={`/prace?orderId=${order.id}`}
                         className="text-water-600 hover:underline mt-2 text-[12px]">
                  Pokaż w rejestrze prac →
                </NavLink>
              )}
            </div>
          </div>
        </aside>
      </div>

      <ZlecenieFormModal open={editOpen} initial={order} clients={clients} meta={meta}
                         onClose={() => setEditOpen(false)}
                         onSaved={() => { setEditOpen(false); reload(); }}/>
    </div>
  );
}

function Row({ label, value, hint, tone }: {
  label: string; value: string; hint?: string; tone?: 'red' | 'amber';
}) {
  const color = tone === 'red' ? 'text-red-600 font-semibold'
    : tone === 'amber' ? 'text-amber-700' : 'text-ink-800';
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-500">{label}</span>
      <span className={'text-right ' + color}>
        {value}
        {hint && <div className="text-[11px] text-ink-400 font-normal">{hint}</div>}
      </span>
    </div>
  );
}
